import {
    type CommentPick,
    type DashboardToHostMessage,
    type HostToDashboardMessage,
    buildDashboardDocument
} from '@/lib/dashRuntime';
import {
    type DashboardReference,
    elementReference,
    sourceReference,
    upsertReference
} from '@/lib/dashboardReferences';
import {
    DashboardRequestError,
    SHARED_DASHBOARDS_KEY,
    deleteDashboard,
    exportDashboardAsApp,
    getDashboard,
    renameDashboard,
    shareDashboard
} from '@/lib/dashboards-api';
import { cn } from '@/lib/utils';
import { ShieldAlert } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import useSWR, { mutate as refreshCache } from 'swr';

import { useConfig } from '@chainlit/react-client';

import { useTheme } from '@/components/ThemeProvider';

import { dashboardReferencesState } from '@/state/chat';

import { DashboardCommentPopover } from './DashboardCommentPopover';
import { DashboardMenu } from './DashboardMenu';
import { type DashboardMode, DashboardModeToggle } from './DashboardModeToggle';
import { DashboardNoAccess } from './DashboardNoAccess';
import { DashboardNotFound } from './DashboardNotFound';
import { DashboardPageBar } from './DashboardPageBar';
import { DashboardSources } from './DashboardSources';
import { DashboardStatus } from './DashboardStatus';
import { DashboardWorkspaceBar } from './DashboardWorkspaceBar';
import { useDashboardData } from './useDashboardData';
import { DASHBOARD_LIST_KEY } from './useDashboardTitles';

const SPIN_TURN_MS = 2500;

interface DashboardFrameProps {
    dashboardId: string;
    leading?: ReactNode;
    onComment?: (reference: DashboardReference, text: string) => void;
    viewOnly?: boolean;
    published?: boolean;
}

export function DashboardFrame({
    dashboardId,
    leading,
    onComment,
    viewOnly = false,
    published = false
}: DashboardFrameProps) {
    const { variant } = useTheme();
    const { config } = useConfig();
    const organizationArtifactsEnabled =
        config?.features?.organization_artifacts_enabled === true;
    const rootRef = useRef<HTMLDivElement>(null);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const canvasRef = useRef<HTMLDivElement>(null);
    const [loadCount, setLoadCount] = useState(0);
    const [showSources, setShowSources] = useState(false);
    const [commenting, setCommenting] = useState(false);
    const [fullscreen, setFullscreen] = useState(false);
    const [activeTab, setActiveTab] = useState('');
    const [commentTarget, setCommentTarget] = useState<CommentPick | null>(
        null
    );
    const [deleted, setDeleted] = useState(false);
    const setReferences = useSetRecoilState(dashboardReferencesState);
    const [spinning, setSpinning] = useState(false);

    const {
        data: dashboard,
        error,
        isLoading: isDashboardLoading,
        mutate
    } = useSWR(
        published
            ? ['dashboard', dashboardId, 'published']
            : ['dashboard', dashboardId],
        () => getDashboard(dashboardId, published),
        {
            revalidateOnFocus: false,
            revalidateOnReconnect: false
        }
    );

    const {
        datasets,
        variables,
        isLoading,
        refresh,
        refreshAll,
        setVariables
    } = useDashboardData(dashboard, published);

    const documentHtml = useMemo(
        () =>
            dashboard ? buildDashboardDocument(dashboard.html, variant) : null,
        [dashboard, variant]
    );

    useEffect(() => {
        if (isLoading) {
            setSpinning(true);
            return;
        }
        const timer = setTimeout(() => setSpinning(false), SPIN_TURN_MS);
        return () => clearTimeout(timer);
    }, [isLoading]);

    useEffect(() => {
        const onMessage = (event: MessageEvent<DashboardToHostMessage>) => {
            if (event.source !== iframeRef.current?.contentWindow) return;
            if (event.data?.dash === 'ready')
                setLoadCount((count) => count + 1);
            if (event.data?.dash === 'refresh') refresh(event.data.id);
            if (event.data?.dash === 'variables')
                setVariables(event.data.variables);
            if (event.data?.dash === 'comment' && dashboard?.isOwner) {
                setCommentTarget(event.data);
            }
            if (event.data?.dash === 'commentCancel') setCommentTarget(null);
            if (event.data?.dash === 'tab') setActiveTab(event.data.label);
            if (event.data?.dash === 'pick' && dashboard?.isOwner) {
                const reference = elementReference(
                    dashboard,
                    event.data,
                    dashboard.datasets
                );
                setReferences((current) => upsertReference(current, reference));
            }
        };
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, [refresh, setVariables, dashboard, setReferences]);

    useEffect(() => {
        if (loadCount === 0) return;
        const message: HostToDashboardMessage = {
            dash: 'state',
            datasets,
            variables
        };
        iframeRef.current?.contentWindow?.postMessage(message, '*');
    }, [datasets, variables, loadCount]);

    useEffect(() => {
        if (loadCount === 0) return;
        const message: HostToDashboardMessage = {
            dash: 'mode',
            editing: showSources,
            commenting
        };
        iframeRef.current?.contentWindow?.postMessage(message, '*');
    }, [showSources, commenting, loadCount]);

    useEffect(() => {
        if (!viewOnly) return;
        const onChange = () =>
            setFullscreen(Boolean(document.fullscreenElement));
        document.addEventListener('fullscreenchange', onChange);
        return () => document.removeEventListener('fullscreenchange', onChange);
    }, [viewOnly]);

    const toggleFullscreen = () => {
        if (document.fullscreenElement) document.exitFullscreen();
        else rootRef.current?.requestFullscreen().catch(() => undefined);
    };

    const closeComment = () => {
        setCommentTarget(null);
        const message: HostToDashboardMessage = { dash: 'commentClear' };
        iframeRef.current?.contentWindow?.postMessage(message, '*');
    };

    const chooseMode = (mode: DashboardMode) => {
        setShowSources(mode === 'sources');
        setCommenting(mode === 'comment');
        setCommentTarget(null);
    };

    if (deleted) {
        return (
            <p className="p-4 text-muted-foreground text-sm">
                This dashboard has been deleted.
            </p>
        );
    }

    if (isDashboardLoading) {
        return (
            <p className="p-4 text-muted-foreground text-sm">
                Loading dashboard…
            </p>
        );
    }

    if (error instanceof DashboardRequestError && error.status === 404) {
        return <DashboardNotFound />;
    }

    if (error instanceof DashboardRequestError && error.status === 403) {
        return <DashboardNoAccess />;
    }

    if (error || !dashboard || !documentHtml) {
        return (
            <p className="p-4 text-destructive text-sm">
                {error instanceof Error
                    ? error.message
                    : 'The dashboard could not be loaded.'}
            </p>
        );
    }

    const states = dashboard.datasets.map((dataset) => datasets[dataset.id]);
    const failed = states.filter((state) => state?.status === 'error').length;
    const loaded = states.filter((state) => state?.status === 'ok').length;
    const summary = isLoading
        ? 'Loading…'
        : failed === 0
        ? 'All loaded'
        : loaded === 0
        ? 'Nothing loaded'
        : `${failed} of ${states.length} failed`;

    const noAccess = dashboard.datasets.filter(
        (dataset) => datasets[dataset.id]?.accessDenied
    );

    const status = (
        <DashboardStatus
            variant={viewOnly ? 'big' : 'app'}
            summary={summary}
            failed={failed > 0}
            loading={isLoading}
            spinning={spinning}
            onRefresh={refreshAll}
        />
    );

    const menu = dashboard.isOwner ? (
        <DashboardMenu
            dashboardId={dashboard.id}
            title={dashboard.title}
            updatedAt={dashboard.updatedAt}
            onRename={async (title) => {
                const renamed = await renameDashboard(dashboard, title);
                await mutate(renamed, { revalidate: false });
                await refreshCache(DASHBOARD_LIST_KEY);
            }}
            onExportToApps={async (name, grouping, access) => {
                const { shared } = await exportDashboardAsApp(
                    dashboard.id,
                    name,
                    grouping,
                    access
                );
                if (!shared) return;
                await mutate();
                await refreshCache(DASHBOARD_LIST_KEY);
                await refreshCache(SHARED_DASHBOARDS_KEY);
            }}
            shared={Boolean(dashboard.sharedAt)}
            onToggleShare={
                organizationArtifactsEnabled
                    ? async (shared) => {
                          const updated = await shareDashboard(
                              dashboard.id,
                              shared
                          );
                          await mutate(
                              { ...dashboard, sharedAt: updated.sharedAt },
                              { revalidate: false }
                          );
                          await refreshCache(DASHBOARD_LIST_KEY);
                          await refreshCache(SHARED_DASHBOARDS_KEY);
                      }
                    : undefined
            }
            onDelete={async () => {
                await deleteDashboard(dashboard.id);
                await refreshCache(DASHBOARD_LIST_KEY);
                setDeleted(true);
            }}
        />
    ) : (
        <span className="px-2 text-sm truncate">{dashboard.title}</span>
    );

    const modes = dashboard.isOwner ? (
        <DashboardModeToggle
            mode={commenting ? 'comment' : showSources ? 'sources' : 'use'}
            canComment={Boolean(onComment)}
            onChange={chooseMode}
        />
    ) : null;

    return (
        <div
            ref={rootRef}
            className={cn(
                'flex flex-col h-full min-h-0',
                fullscreen && 'bg-background'
            )}
        >
            {viewOnly ? (
                !fullscreen && (
                    <DashboardPageBar
                        title={dashboard.title}
                        tab={activeTab}
                        status={status}
                        fullscreen={fullscreen}
                        onToggleFullscreen={toggleFullscreen}
                    />
                )
            ) : (
                <DashboardWorkspaceBar
                    leading={leading}
                    menu={menu}
                    status={status}
                    modes={modes}
                />
            )}
            {!isLoading && noAccess.length > 0 && (
                <div
                    role="alert"
                    className="flex items-start gap-2 bg-destructive/10 px-3 py-2 border-b border-border text-destructive text-sm"
                >
                    <ShieldAlert className="mt-0.5 w-4 h-4 shrink-0" />
                    <p>
                        You probably do not have access to some of this data, so
                        those numbers are empty. Ask an admin to give you access
                        to {noAccess.map((dataset) => dataset.title).join(', ')}
                        .
                    </p>
                </div>
            )}
            <div ref={canvasRef} className="relative flex flex-1 min-h-0">
                <iframe
                    ref={iframeRef}
                    srcDoc={documentHtml}
                    sandbox="allow-scripts"
                    title={dashboard.title}
                    className="flex-1 border-0 min-w-0 h-full"
                />
                {commentTarget && onComment && (
                    <DashboardCommentPopover
                        key={commentTarget.selector}
                        anchor={commentTarget.rect}
                        container={{
                            width: canvasRef.current?.clientWidth ?? 0,
                            height: canvasRef.current?.clientHeight ?? 0
                        }}
                        breadcrumb={commentTarget.breadcrumb}
                        onCancel={closeComment}
                        onSubmit={(text) => {
                            onComment(
                                elementReference(
                                    dashboard,
                                    commentTarget,
                                    dashboard.datasets
                                ),
                                text
                            );
                            closeComment();
                        }}
                    />
                )}
                {showSources && dashboard.isOwner && !viewOnly && (
                    <DashboardSources
                        datasets={dashboard.datasets}
                        states={datasets}
                        onRefresh={refresh}
                        onEdit={(dataset) =>
                            setReferences((current) =>
                                upsertReference(
                                    current,
                                    sourceReference(dashboard, dataset)
                                )
                            )
                        }
                        onClose={() => setShowSources(false)}
                    />
                )}
            </div>
        </div>
    );
}
